/*
 * FALLBACK ONLY: reached solely from the legacy branch of firstPass, when no
 * aligner/WhisperX helper answers (phones). Kept working per project
 * constraint — do not extend.
 */

/**
 * Alignment quality — "are these timings physically possible for this audio?"
 *
 * WHY THIS EXISTS
 * ---------------
 * `refineBoundaries` has a guard that compares speech coverage before and after
 * and keeps the better one. That guard answers "did refinement help?", which is
 * NOT the same question as "is the result any good?". A draw whose timings are
 * wrong everywhere can be refined into timings that are *less* wrong everywhere
 * and still be unusable — the guard is happy, the user hears drift.
 *
 * This module answers the absolute question, on the audio itself, with no
 * reference to the model's own opinion:
 *
 *   spanOnSpeech  fraction of labelled time that lands on detected speech.
 *                 Low  => segments are sitting in silence.
 *   speechRecall  fraction of detected speech that some segment covers.
 *                 Low  => words were spoken that nothing is labelling.
 *   implausible   segments whose duration is impossible for their own text
 *                 (a 90 ms five-letter word). These are the invented splits
 *                 that make a burst's labels feel offset from its audio.
 *   emptyBursts   continuous speech runs with no segment over them at all.
 *   tailError     how far the last segment's end sits from the real duration.
 *
 * The scalar {@link AlignmentScore.score} exists so two candidate draws can be
 * compared and the better one kept; the components exist so a human reading the
 * log can see *which* way it failed.
 *
 * WHAT IT DOES NOT MEASURE
 * ------------------------
 * Whether the right WORD is at the right place. Every check here is about
 * physical plausibility against the energy envelope, and a draw can satisfy all
 * of them while having the labels of one burst sitting over the audio of the
 * next — measured: two draws of the same file scored 0.81 and 0.86, and the
 * higher-scoring one placed a word 1.66 s from where the lower-scoring one (and
 * a careful listen) put it. Only isolation QA and a human ear can catch that.
 * Treat a good score as "these timings are not obviously impossible", never as
 * "these labels are correct".
 */

import { round3 } from '../../lib/num';
import type { RawSegment } from '../../types';
import { extractMono } from '../audioSlicer';
import { isPathological } from './burstResegment';
// Straight from the VAD, not laundered through `burstResegment`'s re-export:
// that indirection was one of the two edges of the gemini -> pipelineLog ->
// alignmentScore -> burstResegment -> gemini import cycle.
import { computeSpeechEnvelope, findSpeechBursts, type Burst, type SpeechEnvelope } from '../vad';

export interface AlignmentScore {
  /** Overall 0..1, higher is better. See {@link scoreAlignmentWith} for the mix. */
  score: number;
  /** Fraction of labelled time landing on detected speech, 0..1. */
  spanOnSpeech: number;
  /** Fraction of detected speech covered by some segment, 0..1. */
  speechRecall: number;
  /** Segments whose duration is impossible for their label. */
  implausibleCount: number;
  /** {@link implausibleCount} over the segment count, 0..1. */
  implausibleFrac: number;
  /** Speech bursts with no segment over them. */
  emptyBursts: number;
  /** Total bursts found. */
  totalBursts: number;
  /** Seconds of detected speech that no segment covers. */
  unlabelledSpeechSec: number;
  /** Seconds of detected speech in total. */
  totalSpeechSec: number;
  /** End of the last segment, seconds. */
  lastEndSec: number;
  /** Real audio duration, seconds. */
  durationSec: number;
  /** `lastEndSec - durationSec`; negative means the draw stops short. */
  tailErrorSec: number;
  /** Segment count scored. */
  segmentCount: number;
}

/**
 * Weights for the scalar. Recall and span are the two that a listener actually
 * perceives as "the label does not match what I hear", so they carry the mix;
 * implausible durations are the mechanism behind most of it and get a third.
 * Empty bursts are already reflected in recall, so they only trim.
 */
const W_SPAN = 0.35;
const W_RECALL = 0.35;
const W_PLAUSIBLE = 0.3;
/** Subtracted per empty burst, capped — a nudge, not a cliff. */
const EMPTY_BURST_PENALTY = 0.02;
const MAX_EMPTY_BURST_PENALTY = 0.15;

/**
 * A draw scoring below this is re-rolled (see `MAX_DRAWS` in firstPass.ts).
 *
 * Calibrated by scoring real draws of the user's 44.8 s Jordanian-Arabic sample
 * with this exact function:
 *
 *   0.711  a draw whose timeline collapsed — its last timestamp was 27.70 s of
 *          a 44.80 s file (span 0.79, recall 0.92, 12 impossible, 6/29 empty)
 *   0.808  the run the user reported as drifting
 *          (span 0.88, recall 0.87, 9 impossible, 3/29 empty)
 *   0.857  the best draw observed on this file
 *          (span 0.94, recall 0.98, 13 impossible, 2/29 empty)
 *
 * 0.85 is the only cut that puts the complaint on the reject side while still
 * accepting the best draw available. An earlier 0.80 was set from a different
 * VAD's numbers and would have passed the user's run unchanged — which is the
 * exact failure this mechanism exists to prevent.
 *
 * Three points on one recording is thin calibration. It is deliberately paired
 * with a hard cap of two draws: when both score below the line the better one
 * is kept anyway, so a mis-set threshold costs one extra API call, never a
 * failed run or a loop.
 */
export const GOOD_ALIGNMENT_SCORE = 0.85;

/** Reusable envelope, so a caller scoring two draws does not rebuild it twice. */
export interface ScoringContext {
  env: SpeechEnvelope;
  bursts: Burst[];
  durationSec: number;
  totalSpeechSec: number;
}

/** Build the audio-side half of the score once. */
export function buildScoringContext(buffer: AudioBuffer): ScoringContext {
  const durationSec = buffer.duration;
  const env = computeSpeechEnvelope(extractMono(buffer, 0, durationSec), buffer.sampleRate);
  return {
    env,
    bursts: findSpeechBursts(env),
    durationSec,
    totalSpeechSec: env.speechFrames.length * env.hopSec,
  };
}

/** Score `segments` against already-analysed audio. */
export function scoreAlignmentWith(
  ctx: ScoringContext,
  segments: readonly RawSegment[],
): AlignmentScore {
  const { env, bursts, durationSec, totalSpeechSec } = ctx;
  const hop = env.hopSec;
  const frames = env.mask.length;

  const empty: AlignmentScore = {
    score: 0,
    spanOnSpeech: 0,
    speechRecall: 0,
    implausibleCount: 0,
    implausibleFrac: 0,
    emptyBursts: bursts.length,
    totalBursts: bursts.length,
    unlabelledSpeechSec: totalSpeechSec,
    totalSpeechSec,
    lastEndSec: 0,
    durationSec,
    tailErrorSec: -durationSec,
    segmentCount: 0,
  };
  if (segments.length === 0 || frames === 0) return empty;

  // covered[] doubles as the recall mask and the empty-burst test
  const covered = new Uint8Array(frames);
  let span = 0;
  let hit = 0;
  let implausible = 0;

  for (const s of segments) {
    const from = Math.max(0, Math.floor(s.startSec / hop));
    const to = Math.min(frames, Math.ceil(s.endSec / hop));
    for (let i = from; i < to; i++) {
      covered[i] = 1;
      span++;
      if (env.mask[i]) hit++;
    }
    if (isPathological(s)) implausible++;
  }

  let speechCovered = 0;
  for (let i = 0; i < frames; i++) if (env.mask[i] && covered[i]) speechCovered++;

  let emptyBursts = 0;
  for (const b of bursts) {
    const from = Math.max(0, Math.floor(b.startSec / hop));
    const to = Math.min(frames, Math.ceil(b.endSec / hop));
    let any = false;
    for (let i = from; i < to && !any; i++) if (covered[i]) any = true;
    if (!any) emptyBursts++;
  }

  const spanOnSpeech = span > 0 ? hit / span : 0;
  const speechFrames = env.speechFrames.length;
  const speechRecall = speechFrames > 0 ? speechCovered / speechFrames : 0;
  const implausibleFrac = implausible / segments.length;
  const lastEndSec = segments[segments.length - 1].endSec;

  const penalty = Math.min(MAX_EMPTY_BURST_PENALTY, emptyBursts * EMPTY_BURST_PENALTY);
  const score = Math.max(
    0,
    W_SPAN * spanOnSpeech + W_RECALL * speechRecall + W_PLAUSIBLE * (1 - implausibleFrac) - penalty,
  );

  return {
    score: round3(score),
    spanOnSpeech: round3(spanOnSpeech),
    speechRecall: round3(speechRecall),
    implausibleCount: implausible,
    implausibleFrac: round3(implausibleFrac),
    emptyBursts,
    totalBursts: bursts.length,
    unlabelledSpeechSec: round3((speechFrames - speechCovered) * hop),
    totalSpeechSec: round3(totalSpeechSec),
    lastEndSec: round3(lastEndSec),
    durationSec: round3(durationSec),
    tailErrorSec: round3(lastEndSec - durationSec),
    segmentCount: segments.length,
  };
}

/**
 * Is this draw's *time axis* obviously broken, before any refinement?
 *
 * Cheap, text-free checks against the real duration. A draw that runs past the
 * end of the file, or stops well short of it, has invented its own timeline —
 * refinement will stretch it onto the audio and produce confident nonsense.
 */
export function drawTimingFault(
  segments: readonly RawSegment[],
  durationSec: number,
): string | null {
  if (segments.length === 0) return 'no segments';
  const last = segments[segments.length - 1].endSec;
  if (last > durationSec + 1) {
    return `last timestamp ${last.toFixed(2)}s exceeds duration ${durationSec.toFixed(2)}s by more than 1s`;
  }
  if (last < durationSec * 0.85) {
    return `last timestamp ${last.toFixed(2)}s undershoots duration ${durationSec.toFixed(2)}s by more than 15%`;
  }
  return null;
}
